import { Box, Button, IconButton, Typography } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';

import { MetaStrip, type MetaItem } from '../../IndividualPlan/MetaStrip';
import { COLORS, TREND_STYLES } from '../../IndividualPlan/tokens';
import type { PeReport } from '../types';
import { schoolYearEndYear } from '../../../core/schoolYear';

const SUMMARY =
  'A district-level evaluation of EL program performance — surfacing the ' +
  "year's headline patterns, school-by-school variation, and cohort-level findings.";

export function formatGeneratedDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const WIDA_DOMAINS = ['listening', 'speaking', 'reading', 'writing'] as const;
const DEFAULT_DOMAIN_MINIMUM = 4;

function titleCase(domain: string): string {
  return domain.charAt(0).toUpperCase() + domain.slice(1);
}

function normalizedDomainMinimums(minimums: Record<string, number>): Record<string, number> {
  const normalized: Record<string, number> = {};
  for (const domain of WIDA_DOMAINS) {
    normalized[domain] = minimums[domain] ?? DEFAULT_DOMAIN_MINIMUM;
  }
  return normalized;
}

function commonDomainFloor(minimums: Record<string, number>): number {
  const counts = new Map<number, number>();
  for (const domain of WIDA_DOMAINS) {
    const value = minimums[domain];
    if (value == null) continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  let floor = DEFAULT_DOMAIN_MINIMUM;
  let bestCount = -1;
  for (const [value, count] of counts) {
    if (count > bestCount || (count === bestCount && value > floor)) {
      bestCount = count;
      floor = value;
    }
  }
  return floor;
}

export type ExitCriterion = { measure: string; label: string; bar: number };

export function exitCriteriaParts(report: PeReport): ExitCriterion[] {
  const config = report.metadata.district_config_snapshot;
  if (config?.CompositeMinimum == null) return [];
  const minimums = normalizedDomainMinimums(config.DomainMinimums ?? {});
  const floor = commonDomainFloor(minimums);
  const parts: ExitCriterion[] = [{ measure: 'composite', label: 'composite', bar: config.CompositeMinimum }];
  for (const domain of WIDA_DOMAINS) {
    const value = minimums[domain];
    if (value != null && value !== floor) {
      parts.push({ measure: domain, label: domain, bar: value });
    }
  }
  return parts;
}

export function exitCriteriaLabel(report: PeReport): string | null {
  const parts = exitCriteriaParts(report);
  if (parts.length === 0) return null;
  return parts
    .map(({ measure, bar }) => `${bar} ${measure === 'composite' ? 'composite' : titleCase(measure)}`)
    .join(' · ');
}

function reclassificationCriteria(report: PeReport): string | null {
  return exitCriteriaLabel(report);
}

export function ProgramEvaluationHeader({
  report,
  showShare = true,
}: {
  report: PeReport;
  showShare?: boolean;
}) {
  const { district, metadata } = report;
  const generatedOn = formatGeneratedDate(metadata.generated_at);
  const criteria = reclassificationCriteria(report);
  const endYear = schoolYearEndYear(district.school_year);
  const eyebrow = endYear == null ? 'Program Evaluation' : `Program Evaluation · ${endYear}`;

  const metaItems: MetaItem[] = [
    { label: 'Report period', value: district.school_year },
    { label: 'Schools', value: String(district.school_count_with_els) },
    { label: 'EL students', value: String(district.el_student_count) },
    { label: 'State framework', value: 'WIDA ACCESS' },
    ...(criteria ? [{ label: 'Exit criteria', value: criteria }] : []),
  ];

  return (
    <Box component="header" sx={{ mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            data-testid="report-eyebrow"
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: COLORS.muted,
              mb: 0.5,
            }}
          >
            {eyebrow}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              component="h1"
              sx={{ fontSize: 32, fontWeight: 700, color: COLORS.ink, lineHeight: 1.2 }}
            >
              {district.name}
            </Typography>
            {generatedOn && (
              <Box
                data-testid="generated-badge"
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: 12,
                  fontWeight: 600,
                  bgcolor: TREND_STYLES.met.bg,
                  color: TREND_STYLES.met.fg,
                  whiteSpace: 'nowrap',
                }}
              >
                {`Generated ${generatedOn}`}
              </Box>
            )}
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted, mt: 0.75, maxWidth: 720 }}>
            {SUMMARY}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <IconButton size="small" sx={{ color: 'rgba(8,8,27,0.54)' }} aria-label="Helpful">
            <ThumbUpAltOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'rgba(8,8,27,0.54)' }} aria-label="Not helpful">
            <ThumbDownAltOutlinedIcon fontSize="small" />
          </IconButton>
          <Box sx={{ width: '1px', height: 24, bgcolor: COLORS.hairline, mx: 0.5 }} />
          <Button
            startIcon={<PrintOutlinedIcon />}
            sx={{
              textTransform: 'none',
              color: COLORS.ink,
              bgcolor: '#FFFFFF',
              border: `1px solid ${COLORS.hairline}`,
              fontWeight: 600,
              px: 2,
              '&:hover': { bgcolor: '#F0F0F5' },
            }}
          >
            Print
          </Button>
          {showShare && (
            <Button
              variant="contained"
              startIcon={<IosShareIcon />}
              sx={{
                textTransform: 'none',
                bgcolor: COLORS.navy,
                fontWeight: 700,
                px: 2,
                '&:hover': { bgcolor: '#23236F' },
              }}
            >
              Share
            </Button>
          )}
        </Box>
      </Box>

      <Box
        data-testid="report-meta"
        sx={{ mt: 1, borderTop: `1px solid ${COLORS.hairlineSoft}` }}
      >
        <MetaStrip items={metaItems} />
      </Box>
    </Box>
  );
}
